import { useContext } from 'react';

import useTheme from '@rover/kibble/themes/useTheme';
import Marker from '@rover/react-lib/src/components/GeoMap/Marker/Marker';
import RoverBrandContext from '@rover/react-lib/src/context/RoverBrandContext';
import { useSearchUIContext } from '@rover/shared/js/providers/SearchUIContextProvider';
import { useActiveSeason } from '@rover/shared/js/providers/SeasonalThemeBoundary/useActiveSeason';
import { RoverBrand } from '@rover/types/src/RoverBrand';

import PumpkinMarker from './PumpkinMarker';

export type MapResult = {
  latitude: string;
  longitude: string;
  title: string;
  rank?: number | null;
};

type Props = MapResult & {
  onClick: () => void;
  index: number;
  showRank: boolean;
};

const ResultMarker = ({
  index,
  latitude,
  longitude,
  rank,
  onClick,
  title,
  showRank,
}: Props): JSX.Element => {
  const { colors, background } = useTheme();
  const { hoveredResultIndex, setHoveredResultIndex } = useSearchUIContext();
  const brand = useContext(RoverBrandContext)?.brand ?? RoverBrand.Rover;
  const season = useActiveSeason();
  const isHovered = hoveredResultIndex === index;
  const sharedProps = {
    title,
    position: {
      lat: parseFloat(latitude),
      lng: parseFloat(longitude),
    },
    onClick,
    zIndex: isHovered ? 21 : index,
    onMouseEnter: () => {
      setHoveredResultIndex(index);
    },
    onMouseLeave: () => {
      setHoveredResultIndex(null);
    },
    content: showRank ? `${rank}` : undefined,
    scale: isHovered ? 1.2 : 1,
  };

  // Seasonal theming is Rover-only, matching useBrandThemeMemo.
  if (brand === RoverBrand.Rover && season === 'halloween') {
    return <PumpkinMarker {...sharedProps} />;
  }

  return (
    <Marker
      {...sharedProps}
      color={
        isHovered ? colors.interactive_link.primary_base : colors.interactive_text.primary_base
      }
      borderColor={showRank ? undefined : background.primary}
    />
  );
};

export default ResultMarker;
